import React, { useEffect, useState } from 'react';
import { PayPalButtons, usePayPalScriptReducer } from "@paypal/react-paypal-js";
import { Text, NumberInput, Loader, Divider, Button, Badge, Modal, Checkbox, Anchor, Group, Stack, Select } from '@mantine/core';
import { IconCheck, IconClock, IconApps } from '@tabler/icons-react';
import { useLocalizedNavigate } from "../../seo/useLocalizedNavigate";
import { useCart } from '../Cart/CartContext';
import { useTranslation } from "react-i18next";
import { DEBUG } from '../..';
import { CartItem, Discount, Product, Cart } from './UseCart';
import { OrderData, LoadOrderDataAsync } from '../OrderList/OrderList';
import { Features, hasFlag, runningPackageEnd, ListReleasedAppNames, allFeatures } from '../../scripts/FeatureCheck';
import { PAYPAL_ENV } from '../../config/paypal';
import classes from './Checkout.module.css';
import { useSession } from '../../auth/SessionContext';

interface CheckoutProviderProps {
  logged_in: boolean;
  isPending: boolean;
  cartProduct: 'lifetime' | 'welcome';
  onApproveNavigation: () => void;
  t: (key: string) => string;
  loadSessionDataAsync: () => Promise<boolean>;
}

interface CheckoutProviderState {
  quantity: number;
  welcomeQuantity: number;
  product: Product;
  orderID: string;
  onApproveMessage: string;
  onErrorMessage: string;
  logged_in: boolean;
  isPending: boolean;
  showPreError: boolean;
  showPostError: boolean;
  cart: Cart;
  upgradeableKeys: OrderData[];
  // Keys the Welcome Package can be booked onto. Refunded keys are only offered on the
  // sandbox build, where every test purchase produces one.
  packageKeys: OrderData[];
  selectedPackageKey: string;
  tosAccepted: boolean;
}

type UpgradeKeyTupel = [string, string];

const HolidayDiscount: Discount = {
  discount: 5.00,
  start: new Date(2025, 11, 20, 0, 0, 0),
  end: new Date(2025, 11, 31, 23, 59, 59),
  affiliate: ""
}

const NoDiscount: Discount = {
  discount: 0,
  start: new Date(0),
  end: new Date(0),
  affiliate: ""
}

const ACCDRIVE: Product = {
  name: "iR DRIVE",
  value: 25.00,
  tax: 3.99,
  discount: HolidayDiscount
}

const iRDRIVE_NEW: Product = {
  name: "iR DRIVE",
  value: 25.00,
  tax: 3.99,
  discount: HolidayDiscount
}

const iRDRIVE_UPD: Product = {
  name: "iR DRIVE",
  value: 20.00,
  tax: 3.19,
  discount: NoDiscount
}

const WELCOME_PACKAGE: Product = {
  name: "iR DRIVE Welcome Package",
  value: 20.00,
  tax: 3.19,
  discount: NoDiscount
}

const initialState: CheckoutProviderState = {
  quantity: 1,
  welcomeQuantity: 1,
  product: ACCDRIVE,
  orderID: "",
  onApproveMessage: "",
  onErrorMessage: "",
  logged_in: false,
  isPending: false,
  showPreError: false,
  showPostError: false,
  cart: new Cart(iRDRIVE_NEW),
  upgradeableKeys: [],
  packageKeys: [],
  selectedPackageKey: '',
  tosAccepted: false
};

export class CheckoutProvider extends React.Component<CheckoutProviderProps, typeof initialState> {
  
  activeDiscount = 0.00;
  upgradeableKeys: OrderData[] = [];
  cart: Cart;
  
  async create() {
    // Raw list; componentDidMount derives the upgrade candidates and the package
    // targets from it, which are different filters over the same keys.
    // Not logged in, get-orders.php answers with a JSON string rather than a list —
    // anything that is not an array must not reach a .filter() further down.
    const data = await LoadOrderDataAsync();
    return Array.isArray(data) ? data : null;
  }

  constructor(props: CheckoutProviderProps) {
    super(props);
    this.cart = new Cart(iRDRIVE_NEW);
    this.state = {
      ...initialState,
      logged_in: props.logged_in,
      isPending: props.isPending,
      cart: new Cart(iRDRIVE_NEW),
    };
    
    this.onChange = this.onChange.bind(this);
    this.createOrder = this.createOrder.bind(this);
    this.onApprove = this.onApprove.bind(this);
    this.onError = this.onError.bind(this);
    this.onClick = this.onClick.bind(this);
    
    var now = new Date();
    this.activeDiscount = now >= this.state.product.discount.start && 
                          now <= this.state.product.discount.end ? 
                          this.state.product.discount.discount : 0.00;
  }
  
  async componentDidMount() {
    var upgradeable = await this.create();
    if(upgradeable != null)
      this.upgradeableKeys = upgradeable;
    else
      this.upgradeableKeys = [
        {strSteamId:"76561197985207199",id:708,ident:"b5c98f33-56d1-4859-85af-a404c96d797f",owner:"DEV_loki 76561197985207199",discordId:null,created:"2022-04-03T01:04:09",features:3,refunded:false,steamId:76561197985207199,steamIdBindingTime:"2024-11-08T09:27:06.960529",affiliate:"ACC_DRIVE_TEAM"},
        {strSteamId:"76561197985207200",id:9415,ident:"c7033555-e9aa-4f20-8271-44c7082d70ca",owner:"Manuel Ritter ProTest 76561197985207199",discordId:null,created:"2024-03-27T23:27:08.080991",features:1,refunded:false,steamId:76561197985207200,steamIdBindingTime:"2024-03-27T23:29:43.924618",affiliate:""}
      ];
    
    const owned = this.upgradeableKeys;
    // Upgrade offers only make sense for keys that do not have this app yet.
    this.upgradeableKeys = owned.filter(d => !hasFlag(d.features, Features.IRDRIVE));

    this.setState({
      upgradeableKeys: this.upgradeableKeys,
      // A refunded key is dead weight in production, but on the sandbox build every
      // test purchase creates one, so there would be nothing to pick otherwise.
      packageKeys: owned.filter(k => PAYPAL_ENV === 'sandbox' || !k.refunded)
    });
  }
  
  componentDidUpdate(prevProps: CheckoutProviderProps) {
    if (prevProps.isPending !== this.props.isPending) {
      this.setState({ isPending: this.props.isPending });
    }
  }

  onChange(value: any) {
    if(value == "") value = 1;
    this.setState({
      quantity: value,
      orderID: "",
      onApproveMessage: "",
      onErrorMessage: ""
    });
  }
  
  onChangeNew = (value: any) => {
    if(value === "" || value.length === 0) value = 1;
    const updatedCart = this.state.cart;
    updatedCart.Items[0].quantity = value;
    this.forceUpdate();
  }

  upgradeKey = (key: string) => {
    const updatedCart = this.state.cart;
    updatedCart.AddUpgradeKey(iRDRIVE_UPD, key);
    this.forceUpdate();
  }

  createWelcomeOrder(data: Record<string, unknown>, actions: any) {
    const ref = localStorage.getItem("ref") ?? "";
    return actions.order.create({
      purchase_units: [{
        amount: {
          value: (this.state.welcomeQuantity * WELCOME_PACKAGE.value).toFixed(2),
          breakdown: { item_total: { value: (this.state.welcomeQuantity * WELCOME_PACKAGE.value).toFixed(2), currency_code: "EUR" } }
        },
        items: [{
          name: WELCOME_PACKAGE.name,
          quantity: this.state.welcomeQuantity,
          unit_amount: { value: WELCOME_PACKAGE.value, currency_code: "EUR" },
          image_url: "https://simracingsolutions.de/email-assets/logos/ir-drive.png",
          url: "https://accdrive.com/"
        }]
      }]
    }).then((orderID: any) => {
      this.setState({ orderID });
      return this.logWelcomeOrder(ref, orderID).then((logOk: boolean) => {
        if (logOk) return orderID;
        this.setState({ showPreError: true });
        return Promise.reject("Order failed");
      });
    });
  }

  async logWelcomeOrder(ref: string | null, id: string) {
    const formData = new FormData();
    formData.append('amount', this.state.welcomeQuantity.toString());
    formData.append('affiliate', ref ?? "");
    formData.append('id', id);
    formData.append('st', 'CREATED');
    formData.append('productType', 'welcome');
    try {
      const response = await fetch('/dyn/process-order.php?mode=create', { method: 'POST', body: formData });
      if (!response.ok) throw new Error('Network response was not ok');
      const result = await response.json();
      return result.success === true;
    } catch (error: any) {
      if(DEBUG) console.error("Order Error", error.toString());
    }
    return false;
  }

  async handleWelcomeSubmit(ref: string | null, email: string, id: string, status: string) {
    const formData = new FormData();
    formData.append('amount', this.state.welcomeQuantity.toString());
    formData.append('affiliate', ref ?? "");
    formData.append('em', email);
    formData.append('id', id);
    formData.append('st', status);
    formData.append('productType', 'welcome');
    // Empty = issue a new key. The server re-checks that this Steam account owns the
    // key before booking anything onto it.
    formData.append('pk', this.state.selectedPackageKey);
    try {
      const response = await fetch('/dyn/process-order.php?mode=confirm', { method: 'POST', body: formData });
      if (!response.ok) throw new Error('Network response was not ok');
      const result = await response.json();
      return result.success === true;
    } catch (error: any) {
      if(DEBUG) console.error("Order Error", error.toString());
    }
    return false;
  }

  onApproveWelcome(data: any, actions: any) {
    let app = this;
    return actions.order.capture().then(async function (details: any) {
      const ref = localStorage.getItem("ref") ?? "";
      await app.handleWelcomeSubmit(ref, details.payer.email_address, details.id, details.status).then((state: boolean) => {
        if (state) {
          app.setState({ onApproveMessage: `Transaction completed by ${details.payer.name.given_name}!` });
        } else {
          app.setState({ showPostError: true });
          return Promise.reject("Order failed");
        }
      });
      app.props.onApproveNavigation();
    });
  }

  createOrder(data: Record<string, unknown>, actions: any) {
    const ref = localStorage.getItem("ref") ?? "";
    const cartCalc = this.calculateCart();

    var itemList = [];
    if(cartCalc.quantityNew > 0){
      itemList.push({
        name: this.cart.Items[0].product.name,
        description: "New",
        quantity: cartCalc.quantityNew,
        unit_amount: {
          value: cartCalc.valueNewSingle,
          currency_code: "EUR"
        },
        image_url: "https://simracingsolutions.de/email-assets/logos/ir-drive.png",
        url: "https://accdrive.com/"
      });
    }
    
    if(cartCalc.quantityUpgrade > 0){
      itemList.push({
        name: this.cart.Items[0].product.name,
        description: "Upgrade",
        quantity: cartCalc.quantityUpgrade,
        unit_amount: {
          value: cartCalc.valueUpgradeSingle,
          currency_code: "EUR"
        },
        image_url: "https://simracingsolutions.de/email-assets/logos/ir-drive.png",
        url: "https://accdrive.com/"
      });
    }
    
    return actions.order
      .create({
        purchase_units: [
          {
            amount: {
              value: cartCalc.totalValue.toFixed(2),
              breakdown: {
                item_total: {
                  value: cartCalc.totalValue.toFixed(2),
                  currency_code: "EUR"
                }
              }
            },
            items: itemList
          }
        ]
      })
      .then((orderID: any) => {
        this.setState({ orderID: orderID });
        return this.logOrder(ref, orderID).then((logOk: boolean) => {
          if (logOk) {
            return orderID; // Zahlung geht weiter
          } else {
            this.setState({ showPreError: true });
            return Promise.reject("Order failed"); // Zahlung wird abgebrochen
          }
        });
      });
  }

  async logOrder(ref: string | null, id: string) {
    const formData = new FormData();
    formData.append('amount', this.state.cart.Quantity().toString());
    formData.append('affiliate', ref ?? "");
    formData.append('id', id);
    formData.append('st', 'CREATED');

    try {
      const response = await fetch('/dyn/process-order.php?mode=create', {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const result = await response.json();
      if (result.success === true) {
        return true;
      } else {
        if (DEBUG) {
          console.error("Order creation failed:", result.error ?? result);
        }
        return false;
      }
    } catch (error: any) {
      if(DEBUG) console.error("Order Error", error.toString());
    }
    return false;
  }

  async handleSubmit(ref: string | null, givenName: string, surName: string, email: string, id: string, status: string) {
    const formData = new FormData();
    const cartCalc = this.calculateCart();
    const quantity = (cartCalc.quantityNew + cartCalc.quantityUpgrade);
    const { cart, upgradeableKeys } = this.state;
    formData.append('amount', quantity.toString());
    formData.append('affiliate', ref ?? "");
    formData.append('em', email);
    formData.append('id', id);
    formData.append('st', status);
    var keys: UpgradeKeyTupel[] = [];
    console.log(cart);
    console.log(cart.Items);
    cart.Items.forEach(element => {
      console.log(element);
      if(element != null && element.upgradeKey != null){
        var uk = upgradeableKeys.find(k => k.ident == element.upgradeKey);
        if(uk != undefined && uk.features != undefined){
          keys.push([element.upgradeKey,(uk.features | Features.IRDRIVE).toString()])
        } else {
          keys.push([element.upgradeKey,Features.IRDRIVE.toString()])
        }
      }
    });
      formData.append('uk', JSON.stringify(keys));

    try {
      var response: Response;
      response = await fetch('/dyn/process-order.php?mode=new', {
        method: 'POST',
        body: formData,
      });
        
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const result = await response.json();
      if (result.success === true) {
        return true;
      } else {
        if (DEBUG) {
          console.error("Order processing failed:", result.error ?? result);
        }
        return false;
      }
    } catch (error: any) {
      if(DEBUG) console.error("Order Error", error.toString());
    }
    return false;
  }

  async handleFin(ref: string | null, id: string, status: string, quantity: string) {
    const formData = new FormData();
    
    formData.append('amount', quantity);
    formData.append('affiliate', ref ?? "");
    formData.append('id', id);
    formData.append('st', status);
    try {
      var response: Response;
      response = await fetch('/dyn/process-order.php?mode=fin', {
        method: 'POST',
        body: formData,
      });
        
      if (!response.ok) {
        throw new Error('Network response was not ok');
      }
      
      const result = await response.json();
    } catch (error: any) {
      if(DEBUG) console.error("Order Error", error.toString());
    }
  }

  onApprove(data: any, actions: any) {
    let app = this;
    const { cart, upgradeableKeys } = this.state;
    
    return actions.order.capture().then(async function (details: any) {
      const ref = localStorage.getItem("ref") ?? "";
      var quantityTotal = 0;
      /*for(var i = 0; i < cart.Items.length; i++){
        quantityTotal += cart.Items[i].quantity;
        await app.handleSubmit(
          ref, 
          details.payer.name.given_name, 
          details.payer.name.surname, 
          details.payer.email_address, 
          details.id, 
          details.status,
          i
        );
      }*/
      await app.handleSubmit(
        ref, 
        details.payer.name.given_name, 
        details.payer.name.surname, 
        details.payer.email_address, 
        details.id, 
        details.status
      ).then((state: boolean) => {
        if (state) {
          app.setState({
            onApproveMessage: `Transaction completed by ${details.payer.name.given_name}!`
          });
        }else{
          app.setState({ showPostError: true });
          return Promise.reject("Order failed"); // Zahlung wird abgebrochen
        }
      });
      //await app.handleFin(ref, details.id, details.status, quantityTotal.toString());
      
      app.setState({
        onApproveMessage: `Transaction completed by ${details.payer.name.given_name}!`
      });
      
      app.props.onApproveNavigation();
    });
  }

  onError(err: Record<string, unknown>) {
    this.setState({
      onErrorMessage: err.toString()
    });
  }

  onClick() {
    if(DEBUG) console.log("When clicked, amount was", this.state.quantity);
  }

  calculateCart() {
    const { cart } = this.state;
    var now = new Date();
    var totalValue = 0;
    var quantityNew = 0;
    var valueNew = 0;
    var valueNewSingle = -1;
    var quantityUpgrade = 0;
    var valueUpgrade = 0;
    var valueUpgradeSingle = -1;
    
    for(var i = 0; i < cart.Items.length; i++){
      var value = cart.Items[i].product.value - 
        (now >= cart.Items[i].product.discount.start && 
         now <= cart.Items[i].product.discount.end ? 
         cart.Items[i].product.discount.discount : 0.00);
      
      totalValue += cart.Items[i].quantity * value;
      
      if(i == 0){
        valueNew = cart.Items[i].quantity * value;
        quantityNew = cart.Items[i].quantity;
        if(valueNewSingle === -1)
          valueNewSingle = value;
      } else {
        valueUpgrade += cart.Items[i].quantity * value;
        quantityUpgrade += 1;
        if(valueUpgradeSingle === -1)
          valueUpgradeSingle = value;
      }
    }
    
    return {
      totalValue, 
      quantityNew, 
      valueNew, 
      valueNewSingle, 
      quantityUpgrade, 
      valueUpgrade, 
      valueUpgradeSingle
    };
  }

  packageKeyLabel = (key: OrderData): string => {
    const { t } = this.props;
    const apps = ListReleasedAppNames(allFeatures(key)).join(', ') || t('cart.welcome.targetKeyNew');
    const short = key.ident.slice(0, 8);
    const running = runningPackageEnd(key);
    const suffix = running
      ? ` · ${t('cart.welcome.targetKeyRunsUntil')} ${running.toLocaleDateString('de-DE')}`
      : '';
    const refunded = key.refunded ? ' · refunded' : '';
    return `${short}… — ${apps}${suffix}${refunded}`;
  }

  renderWelcome() {
    const { t } = this.props;
    const selected = this.state.packageKeys.find(k => k.ident === this.state.selectedPackageKey);
    // Buying onto a key whose package is still running appends the 90 days instead of
    // starting a second, overlapping one — same arithmetic the server does.
    const runningEnd = selected ? runningPackageEnd(selected) : null;
    const validUntil = runningEnd ? new Date(runningEnd) : new Date();
    validUntil.setDate(validUntil.getDate() + 90);
    const validUntilStr = validUntil.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
    return (
      <div className={classes.checkoutContainer}>
        {this.state.isPending ? (
          <div className={classes.loaderContainer}><Loader color="var(--mantine-color-blue-filled)" size="xl" /></div>
        ) : (
          <>
            <Stack gap={10} mb="sm">
              <Group gap={6}>
                <Badge color="orange" variant="filled" size="sm" leftSection={<IconClock size={11} />}>
                  {t('cart.welcome.tagline')}
                </Badge>
                <Badge variant="outline" size="sm" style={{ color: 'var(--checkout-fg)', borderColor: 'var(--checkout-border)' }}>
                  {t('cart.welcome.noSub')}
                </Badge>
              </Group>
              <Group gap={6} align="center">
                <IconApps size={13} color="var(--checkout-accent)" />
                <Text size="sm" fw={600} c="var(--checkout-fg)">
                  {t('cart.welcome.validUntilLabel')}: <span style={{ color: 'var(--checkout-accent)' }}>{validUntilStr}</span>
                </Text>
              </Group>
              {this.state.packageKeys.length > 0 && (
                <div>
                  <Text size="xs" fw={700} tt="uppercase" c="var(--checkout-fg-dim)" mb={5}>{t('cart.welcome.targetKeyLabel')}</Text>
                  <Select
                    size="xs"
                    allowDeselect={false}
                    value={this.state.selectedPackageKey}
                    onChange={(value) => this.setState({ selectedPackageKey: value ?? '' })}
                    data={[
                      { value: '', label: t('cart.welcome.targetKeyNew') },
                      ...this.state.packageKeys.map(k => ({ value: k.ident, label: this.packageKeyLabel(k) }))
                    ]}
                  />
                  <Text size="xs" c="var(--checkout-fg-dim)" mt={4}>{t('cart.welcome.targetKeyHint')}</Text>
                </div>
              )}
              <div>
                <Text size="xs" fw={700} tt="uppercase" c="var(--checkout-fg-dim)" mb={5}>{t('cart.welcome.appsIncluded')}</Text>
                <Group gap={4}>
                  {['ACC DRIVE', 'ACC Academy', 'iR DRIVE', 'ACE DRIVE', 'AC DRIVE', 'LMU DRIVE'].map(app => (
                    <Badge key={app} variant="outline" size="xs" radius="sm" style={{ color: 'var(--checkout-fg)', borderColor: 'var(--checkout-border)' }}>{app}</Badge>
                  ))}
                </Group>
              </div>
              <Stack gap={5}>
                {(['cart.welcome.benefit1', 'cart.welcome.benefit2', 'cart.welcome.benefit3'] as const).map(key => (
                  <Group key={key} gap={6} align="flex-start">
                    <IconCheck size={13} color="var(--checkout-ok)" style={{ marginTop: 2, flexShrink: 0 }} />
                    <Text size="xs" c="var(--checkout-fg)">{t(key)}</Text>
                  </Group>
                ))}
              </Stack>
            </Stack>
            <Divider my="sm" color="orange.3" variant="dashed" />
            <div className={classes.summaryContainer}>
              <Text className={classes.summaryTitle}>{t('cart.summary')}</Text>
              <div className={classes.summaryRow}>
                <Text>{WELCOME_PACKAGE.name}</Text>
                <div className={classes.summaryValues}>
                  <Text className={classes.summaryQuantity}>{this.state.welcomeQuantity}x</Text>
                  <Text>{(WELCOME_PACKAGE.value * this.state.welcomeQuantity).toFixed(2)}€</Text>
                </div>
              </div>
              <Divider className={classes.summaryDivider} />
              <div className={classes.summaryTotal}>
                <Text fw={700}>{t('cart.total')}</Text>
                <Text fw={700} size="xl" className={classes.totalAmount}>{(WELCOME_PACKAGE.value * this.state.welcomeQuantity).toFixed(2)}€</Text>
              </div>
            </div>
            <div className={classes.paymentSection}>
              <Checkbox
                mb={"sm"}
                checked={this.state.tosAccepted}
                onChange={() => this.setState({ tosAccepted: !this.state.tosAccepted })}
                label={
                  <Text size="xs">
                    {t('cart.tosCheckbox.prefix')}{' '}
                    <Anchor href="/tos" target="_blank" size="xs">{t('cart.tosCheckbox.tos')}</Anchor>
                    {' '}{t('cart.tosCheckbox.and')}{' '}
                    <Anchor href="/privacy_web" target="_blank" size="xs">{t('cart.tosCheckbox.privacy')}</Anchor>
                  </Text>
                }
              />
              <div style={{ position: 'relative' }}>
                <PayPalButtons
                  style={{ layout: "vertical", color: "blue", shape: "rect" }}
                  createOrder={this.createWelcomeOrder.bind(this)}
                  onApprove={this.onApproveWelcome.bind(this)}
                />
                {!this.state.tosAccepted && (
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backdropFilter: 'blur(3px)', backgroundColor: 'rgba(0,0,0,0.15)', borderRadius: '4px', cursor: 'not-allowed', zIndex: 9999 }} />
                )}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  render() {
    const { cart, upgradeableKeys } = this.state;
    const cartCalc = this.calculateCart();
    const { t } = this.props;
    const now = new Date();
    const isDiscountActive = now >= this.state.product.discount.start &&
                             now <= this.state.product.discount.end;

    return (
      <>
        <Modal
          className={classes.errorModal}
          opened={this.state.showPreError || this.state.showPostError}
          onClose={() => this.setState({ showPostError: false, showPreError: false })}
          title={t("cart.error.title")}
          size="lg"
          centered
          closeOnClickOutside={false}
          closeOnEscape={false}
          withCloseButton={false}
          >
          {this.state.showPreError ? (
            <>
              {t("cart.error.preOrderError")}
            </>
          ) : ( <></> )}
          {this.state.showPostError ? (
            <>
              {t("cart.error.postOrderError")} <b>{this.state.orderID}</b>
            </>
          ) : ( <></> )}
          <Button
              onClick={() => { this.props.loadSessionDataAsync(); this.setState({ showPostError: false, showPreError: false }); }}
              fullWidth
              className={classes.button}
              color={"#004aba"}
              variant="outline">
            {t("cart.error.close")}
          </Button>
        </Modal>
        {this.props.cartProduct === 'welcome' ? (
          this.state.logged_in ? this.renderWelcome() : <></>
        ) : (
          this.state.logged_in ? (
          <div className={classes.checkoutContainer}>
            {this.state.isPending ? (
              <div className={classes.loaderContainer}>
                <Loader color="var(--mantine-color-blue-filled)" size="xl" />
              </div>
            ) : (
              <>
                {/* Upgrade Cards */}
                {upgradeableKeys.length > 0 && (
                  <>
                    <div className={classes.sectionHeader}>
                      <Text className={classes.sectionTitle}>{t('cart.upgrades')}</Text>
                    </div>
                    <div className={classes.upgradeList}>
                      {upgradeableKeys.map((key, index) => {
                        const isUpgraded = cart.HasUpgradeKey(key.ident);
                        const createdDate = new Date(key.created).toLocaleDateString();
                        
                        return (
                          <div className={classes.upgradeCard} key={index}>
                            <div className={classes.upgradeHeader}>
                              <div className={classes.licenseTitle}>
                                <Text className={classes.upgradeTitle}>
                                  {t('cart.license.accDrive')}
                                </Text>
                              </div>
                              
                              {/* Desktop version: Date + Button side by side */}
                              <div className={classes.dateButtonContainer}>
                                <Badge color="blue" variant="light" className={classes.dateBadge}>
                                  {createdDate}
                                </Badge>
                                <Button 
                                  onClick={() => this.upgradeKey(key.ident)} 
                                  className={isUpgraded ? classes.upgradeButton : classes.whiteUpgradeButton}
                                  color={isUpgraded ? "blue" : "white"}
                                  variant="filled"
                                >
                                  {isUpgraded ? t('cart.upgrade.remove') : t('cart.upgrade.upgrade')}
                                </Button>
                              </div>
                              
                              {/* Mobile version: Date (visible on mobile) */}
                              <Badge color="blue" variant="light" className={classes.mobileDateBadge}>
                                {createdDate}
                              </Badge>
                            </div>
                            
                            <div className={classes.upgradeDetails}>
                              <div className={classes.upgradeField}>
                                <Text size="xs" className={classes.fieldLabel}>{t('orders.header.owner')}</Text>
                                <Text className={classes.upgradeValue}>{key.owner}</Text>
                              </div>
                              
                              <div className={classes.upgradeField}>
                                <Text size="xs" className={classes.fieldLabel}>{t('orders.header.key')}</Text>
                                <Text className={classes.upgradeValue} style={{ wordBreak: 'break-all' }}>
                                  {key.ident}
                                </Text>
                              </div>
                              
                              <div className={classes.upgradeField}>
                                <Text size="xs" className={classes.fieldLabel}>{t('orders.header.steam')}</Text>
                                <Text className={classes.upgradeValue}>{key.steamId}</Text>
                              </div>
                            </div>
                            
                            {/* Mobile version: Button below details */}
                            <div className={classes.mobileBtnContainer}>
                              <Button 
                                onClick={() => this.upgradeKey(key.ident)} 
                                className={isUpgraded ? classes.upgradeButton : classes.whiteUpgradeButton}
                                color={isUpgraded ? "blue" : "white"}
                                variant="filled"
                                fullWidth
                              >
                                {isUpgraded ? t('cart.upgrade.remove') : t('cart.upgrade.upgrade')}
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </>
                )}
                
                {/* New Licenses Section */}
                <div className={classes.sectionHeader}>
                  <Text className={classes.sectionTitle}>
                    {t('cart.new')}
                    <span 
                      className={classes.giftInfo} 
                      data-tooltip={t('cart.giftTooltip')}
                    >
                      {t('cart.giftInfo')}
                    </span>
                  </Text>
                </div>

                {/* New License Selector */}
                <div className={classes.licenseCard}>
                  <div className={classes.licenseInfo}>
                    <img src="/logo192.png" alt="iR DRIVE" className={classes.licenseLogo} />
                    <Text className={classes.licenseTitle}>
                      {t('cart.license.newIrDrive')}
                    </Text>
                  </div>
                  <div className={classes.licenseQuantity}>
                    <NumberInput
                      className={classes.quantityInput}
                      min={0}
                      max={50}
                      stepHoldDelay={500}
                      stepHoldInterval={100}
                      allowDecimal={false}
                      allowNegative={false}
                      value={cart.Items[0].quantity}
                      onChange={this.onChangeNew}
                      clampBehavior="strict"
                    />
                  </div>
                </div>
                
                {/* Order Summary */}
                <div className={classes.summaryContainer}>
                  <Text className={classes.summaryTitle}>{t('cart.summary')}</Text>
                  
                  <div className={classes.summaryRow}>
                    <Text>{cart.Items[0].product.name} {t('cart.license.new')}</Text>
                    <div className={classes.summaryValues}>
                      <Text className={classes.summaryQuantity}>{cartCalc.quantityNew}x</Text>
                      
                      {isDiscountActive ? (
                        <div className={classes.priceWithDiscount}>
                          <Text className={classes.originalPrice}>
                            {(cart.Items[0].product.value * cart.Items[0].quantity).toFixed(2)}€
                          </Text>
                          <Text>
                            {cartCalc.valueNew.toFixed(2)}€
                          </Text>
                        </div>
                      ) : (
                        <Text>{cartCalc.valueNew.toFixed(2)}€</Text>
                      )}
                    </div>
                  </div>
                  
                  {cartCalc.quantityUpgrade > 0 && (
                    <div className={classes.summaryRow}>
                      <Text>{cart.Items[0].product.name} {t('cart.license.upgrade')}</Text>
                      <div className={classes.summaryValues}>
                        <Text className={classes.summaryQuantity}>{cartCalc.quantityUpgrade}x</Text>
                        <Text>{cartCalc.valueUpgrade.toFixed(2)}€</Text>
                      </div>
                    </div>
                  )}
                  
                  <Divider className={classes.summaryDivider} />
                  
                  <div className={classes.summaryTotal}>
                    <Text fw={700}>{t('cart.total')}</Text>
                    <Text fw={700} size="xl" className={classes.totalAmount}>{cartCalc.totalValue.toFixed(2)}€</Text>
                  </div>
                </div>
                
                {/* Payment Buttons */}
                <div className={classes.paymentSection}>
                  <Checkbox
                    mb={"sm"}
                    checked={this.state.tosAccepted}
                    onChange={() => this.setState({ tosAccepted: !this.state.tosAccepted })}
                    label={
                      <Text size="xs">
                        {t('cart.tosCheckbox.prefix')}{' '}
                        <Anchor href="/tos" target="_blank" size="xs">
                          {t('cart.tosCheckbox.tos')}
                        </Anchor>
                        {' '}{t('cart.tosCheckbox.and')}{' '}
                        <Anchor href="/privacy_web" target="_blank" size="xs">
                          {t('cart.tosCheckbox.privacy')}
                        </Anchor>
                      </Text>
                    }
                  />
                  <div style={{ position: 'relative' }}>
                    <PayPalButtons
                      style={{ layout: "vertical", color: "blue", shape: "rect" }}
                      createOrder={this.createOrder}
                      onApprove={this.onApprove}
                    />
                    {!this.state.tosAccepted && (
                      <div style={{
                        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                        backdropFilter: 'blur(3px)',
                        backgroundColor: 'rgba(0,0,0,0.15)',
                        borderRadius: '4px',
                        cursor: 'not-allowed',
                        zIndex: 9999
                      }} />
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        ) : (
          <></>
          )
        )}
      </>
    );
  }
}

export const Checkout = (logged_in: any) => {
  const { closeCart, cartProduct } = useCart();
  const navigate = useLocalizedNavigate();
  const { t } = useTranslation("translation");
  const { loadSessionDataAsync } = useSession();
  const redirectToProfile = () => {
    closeCart();
    navigate("/account");
  };
  
  const [{ options, isPending }, dispatch] = usePayPalScriptReducer();
  
  return (
    <CheckoutProvider
      logged_in={logged_in}
      isPending={isPending}
      cartProduct={cartProduct ?? 'lifetime'}
      onApproveNavigation={redirectToProfile}
      t={t}
      loadSessionDataAsync={loadSessionDataAsync}
    />
  );
}